import React, { useState, useMemo, useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { DollarSign, Users, LifeBuoy, TrendingUp, UserCheck, BarChart2, Shield, List, PencilIcon, BookAIcon, Bell, Eye, FileText, Zap, PlusSquare, Settings, Gift, Award } from 'lucide-react';

// Componentes de las secciones del Admin
import AdminDashboard from './admin_sections/AdminDashboard.js';
import InvestmentDetails from './admin_sections/InvestmentDetails.js';
import RegistroFinanciero from './admin_sections/RegistroFinanciero.js';
import GestionDeRetiros from './admin_sections/GestionDeRetiros.js'; // eslint-disable-line
import PequenosDuenosAdmin from './admin_sections/PequenosDuenosAdmin.js';
import EditarPerfilAdmin from './admin_sections/EditarPerfilAdmin.js';
import PagosGlobales from './admin_sections/PagosGlobales.js';
import Reportes from './admin_sections/Reportes.js';
import Soporte from './admin_sections/Soporte.js';
// Auditoría será cargada de forma perezosa (lazy)
import AdminNotifications from './admin_sections/AdminNotifications.js';
import GestionDePagos from './admin_sections/GestionDePagos.js';
import GestionDeUsuarios from './admin_sections/GestionDeUsuarios.js';
import AprobacionUsuarios from './admin_sections/AprobacionUsuarios.js';
import GestionAcelerador from './admin_sections/GestionAcelerador.js';
import CrearAcuerdoManual from './admin_sections/CrearAcuerdoManual.js';
import PermissionsConfig from './admin_sections/PermissionsConfig.js';
import CareerPlanAdmin from './admin_sections/CareerPlanAdmin.js';
import GestionBeneficios from './admin_sections/GestionBeneficios.js';
import AdminNews from './admin_sections/news';
// Auditoría se cargará de forma perezosa cuando todas las importaciones estáticas se hayan declarado.

// Componentes de Layout
import AdminSidebar from '../context/AdminSidebar.js';
import AdminHeader from '../context/AdminHeader.js'; // Ruta correcta

// Constantes y Estilos
import { PERMISSIONS_CONFIG } from '../context/adminPermissions.js';
import { ROLE_DESCRIPTIONS } from '../constants/permissionCatalog.js';
import { useAuth } from '../context/AuthContext.js';
import PermissionGuard from '../components/common/PermissionGuard.js';
import '../styles/AdminLayout.css';
import '../styles/Notifications.css';

const Auditoria = lazy(() => import('./admin_sections/Auditoria.js'));

const SECTION_CONFIG = [
    {
        path: 'dashboard',
        label: 'Vista General',
        icon: BarChart2,
        roles: ['admin'],
        defaultFor: ['admin'],
        permissionsAny: ['panel:dashboard'],
        permissionsAll: ['users:read'],
        component: AdminDashboard,
    },
    {
        path: 'aprobacion-usuarios',
        label: 'Aprobación de Usuarios',
        icon: UserCheck,
        roles: ['admin'],
        component: AprobacionUsuarios,
    },
    {
        path: 'pagos',
        label: 'Solicitudes de Aporte',
        icon: DollarSign,
        roles: ['admin'],
        permissionsAny: ['panel:pagos'],
        permissionsAll: ['agreements:update'],
        component: GestionDePagos,
    },
    {
        path: 'gestionAcelerador',
        label: 'Solicitudes Acelerador',
        icon: Zap,
        roles: ['admin'],
        permissionsAny: ['panel:gestion_acelerador'],
        permissionsAll: ['agreements:update'],
        component: GestionAcelerador,
    },
    {
        path: 'crear-acuerdo',
        label: 'Crear Acuerdo Manual',
        icon: PlusSquare,
        roles: ['admin'],
        permissionsAny: ['panel:crear_acuerdo'],
        permissionsAll: ['agreements:create'],
        component: CrearAcuerdoManual,
    },
    {
        path: 'plan-carrera',
        label: 'Recorrido Gerencial',
        icon: Award,
        roles: ['admin'],
        component: CareerPlanAdmin,
    },
    {
        path: 'registroFinanciero',
        label: 'Registro Financiero',
        icon: Eye,
        roles: ['admin'],
        permissionsAny: ['panel:registro_financiero'],
        permissionsAll: ['finances:assign_gains'],
        component: RegistroFinanciero,
    },
    {
        path: 'investmentDetails',
        label: 'Participaciones Activas',
        icon: List,
        roles: ['admin'],
        permissionsAny: ['panel:inversiones'],
        permissionsAll: ['agreements:update'],
        component: InvestmentDetails,
    },
    {
        path: 'usuarios',
        label: 'Usuarios',
        icon: Users,
        roles: ['admin'],
        permissionsAny: ['panel:usuarios'],
        permissionsAll: ['users:read'],
        component: GestionDeUsuarios,
        componentProps: { permissionsConfig: PERMISSIONS_CONFIG },
    },
    {
        path: 'perfiledit',
        label: 'Editar Perfil',
        icon: PencilIcon,
        roles: ['admin'],
        permissionsAny: ['panel:perfil_admin'],
        permissionsAll: ['users:update'],
        component: EditarPerfilAdmin,
    },
    {
        path: 'retiros',
        label: 'Retiros',
        icon: Shield,
        roles: ['admin'],
        permissionsAny: ['panel:retiros'],
        permissionsAll: ['withdrawals:process'],
        component: GestionDeRetiros,
    },
    {
        path: 'pagosGlobales',
        label: 'Pagos Globales',
        icon: TrendingUp,
        roles: ['admin'],
        permissionsAny: ['panel:pagos_globales'],
        permissionsAll: ['finances:assign_gains'],
        component: PagosGlobales,
    },
    {
        path: 'pequenosDuenos',
        label: 'Pequeños Dueños',
        icon: UserCheck,
        roles: ['admin'],
        permissionsAny: ['panel:pequenos_duenos'],
        permissionsAll: ['agreements:update'],
        component: PequenosDuenosAdmin,
    },
    {
        path: 'club-beneficios',
        label: 'Club de Beneficios',
        icon: Gift,
        roles: ['admin'],
        permissionsAny: ['panel:club_beneficios'],
        permissionsAll: ['platform_settings:update'],
        component: GestionBeneficios,
    },
    {
        path: 'reportes',
        label: 'Reportes',
        icon: FileText,
        roles: ['admin'],
        permissionsAny: ['panel:reportes'],
        permissionsAll: ['users:read'],
        component: Reportes,
    },
    {
        path: 'notificaciones',
        label: 'Notificaciones',
        icon: Bell,
        roles: ['admin'],
        permissionsAny: ['panel:notificaciones'],
        permissionsAll: ['support:handle_tickets'],
        component: AdminNotifications,
    },
    {
        path: 'news',
        label: 'Noticias',
        icon: LifeBuoy,
        roles: ['admin'],
        permissionsAny: [],
        permissionsAll: [],
        component: AdminNews,
    },
    {
        path: 'soporte',
        label: 'Soporte',
        icon: LifeBuoy,
        roles: ['admin', 'support'],
        defaultFor: ['support'],
        permissionsAny: ['panel:soporte', 'support:handle_tickets'],
        permissionsAll: ['support:handle_tickets'],
        component: Soporte,
    },
    {
        path: 'auditoria',
        label: 'Auditoria',
        icon: BookAIcon,
        roles: ['admin'],
        permissionsAny: ['panel:auditoria'],
        permissionsAll: ['maintenance:run_tasks'],
        component: Auditoria,
    },
    {
        path: 'configuracion',
        label: 'Configuración',
        icon: Settings,
        roles: ['admin'],
        permissionsAny: ['panel:configuracion'],
        permissionsAll: ['platform_settings:update'],
        component: PermissionsConfig,
    },
        {
          //  path: 'utilidades',
          //  label: 'Utilidades',
         //   icon: Wrench,
         //   roles: ['admin'],
         //   permissionsAny: ['maintenance:run_tasks'],
         //   permissionsAll: ['maintenance:run_tasks'],
         //   component: lazy(() => import('./admin_sections/UtilidadesAdmin.js'))
        }
];

const AdminApp = ({ user, userProfile, role = 'admin' }) => {
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { hasPermission } = useAuth();
    const roleLabel = ROLE_DESCRIPTIONS[role]?.label || 'Administrador';

    const baseSections = useMemo(
        () => SECTION_CONFIG.filter(section => {
            if (!section.roles || !Array.isArray(section.roles)) return false;
            const roleAllowedDirect = section.roles.includes(role);
            const roleAllowedAsAdmin = section.roles.includes('admin') && (role === 'superadmin' || role === 'support');
            if (!roleAllowedDirect && !roleAllowedAsAdmin) {
                return false;
            }

            const anyPermissions = section.permissionsAny;
            const allPermissions = section.permissionsAll;

            const passesAny = !anyPermissions || anyPermissions.length === 0 || anyPermissions.some(hasPermission);
            const passesAll = !allPermissions || allPermissions.length === 0 || allPermissions.every(hasPermission);

            return passesAny && passesAll;
        }),
        [role, hasPermission]
    );

    const availableSections = useMemo(() => {
        if (role === 'support') {
            if (baseSections.length) {
                return baseSections;
            }
            const supportSection = SECTION_CONFIG.find(section => section.path === 'soporte');
            return supportSection ? [supportSection] : [];
        }

        if ((role === 'admin' || role === 'superadmin') && !baseSections.length) {
            const dashboardSection = SECTION_CONFIG.find(section => section.path === 'dashboard');
            return dashboardSection ? [dashboardSection] : [];
        }

        return baseSections;
    }, [role, baseSections]);

    const defaultSection = useMemo(() => {
        if (!availableSections.length) return null;
        return (
            availableSections.find(section => {
                const defaults = section.defaultFor || [];
                return defaults.includes(role) || (role === 'superadmin' && defaults.includes('admin'));
            }) ||
            availableSections[0]
        );
    }, [availableSections, role]);

    // Detectar si hay tickets de soporte abiertos/en progreso/respondidos
    const [soporteNotificacion, setSoporteNotificacion] = useState(false);
    useEffect(() => {
        // Solo si la sección soporte está disponible
        const soporteSection = availableSections.find(s => s.path === 'soporte');
        if (!soporteSection) {
            setSoporteNotificacion(false);
            return;
        }
        // Escuchar tickets de soporte en Firestore
        const unsubscribe = window.firebase && window.firebase.firestore
            ? window.firebase.firestore().collection('support_tickets')
                .where('status', 'in', ['Abierto', 'En progreso', 'Respondido'])
                .onSnapshot(snapshot => {
                    setSoporteNotificacion(!snapshot.empty);
                })
            : null;
        return () => unsubscribe && unsubscribe();
    }, [availableSections]);

    const sidebarItems = useMemo(
        () => availableSections.map(section => ({
            path: `/${section.path}`,
            label: section.label,
            icon: section.icon,
            soporteBadge: section.path === 'soporte' ? soporteNotificacion : false,
        })),
        [availableSections, soporteNotificacion]
    );

    const allowedPaths = useMemo(() => new Set(sidebarItems.map(item => item.path)), [sidebarItems]);

    const renderSectionElement = (section) => {
        const SectionComponent = section.component;
        if (!SectionComponent) {
            return null;
        }
        return (
            <PermissionGuard anyOf={section.permissionsAny} allOf={section.permissionsAll}>
                <Suspense fallback={<div style={{padding:20}}>Cargando...</div>}>
                    <SectionComponent {...(section.componentProps || {})} />
                </Suspense>
            </PermissionGuard>
        );
    };

    const handleNavClick = (tab) => {
        if (!allowedPaths.has(tab)) {
            if (defaultSection) {
                navigate(`/${defaultSection.path}`, { replace: true });
            }
            return;
        }
        navigate(tab);
        setMobileMenuOpen(false);
    };

    const _initialMount = useRef(true);
    useEffect(() => {
        if (!defaultSection) return;
        const currentPath = location.pathname || '/';
        const isAllowed = sidebarItems.some(item => currentPath.startsWith(item.path));
        // If current path is not allowed for this user, navigate to the default section
        if (!isAllowed) {
            navigate(`/${defaultSection.path}`, { replace: true });
            return;
        }

        // Only perform the automatic admin -> dashboard redirect on the first mount
        // This avoids interfering when the user intentionally clicks the 'Noticias' item.
        if (_initialMount.current) {
            try {
                const isAdminRole = role === 'admin' || role === 'superadmin';
                const isNewsPath = currentPath === '/news' || currentPath.startsWith('/news/');
                if (isAdminRole && isNewsPath && defaultSection && defaultSection.path === 'dashboard') {
                    navigate('/dashboard', { replace: true });
                }
            } catch (e) {
                console.warn('AdminApp navigation guard error:', e);
            } finally {
                _initialMount.current = false;
            }
        }
    }, [defaultSection, location.pathname, navigate, sidebarItems, role]);

    if (!availableSections.length || !defaultSection) {
        return (
            <div className="admin-layout-container">
                <main className="main-content">
                    <p className="message-box warning">Tu rol no tiene secciones habilitadas aún.</p>
                </main>
            </div>
        );
    }

    return (
        <div className="admin-layout-container">
            <AdminSidebar 
                onNavClick={handleNavClick} // Pass the navigation handler
                isMobileMenuOpen={isMobileMenuOpen}
                onToggleMobileMenu={() => setMobileMenuOpen(false)}
                activePath={location.pathname} // Pass the current path for active state
                navItems={sidebarItems}
                roleLabel={roleLabel}
            />
            <div className="main-area">
                <AdminHeader 
                    user={user}
                    userProfile={userProfile}
                    roleLabel={roleLabel}
                    onToggleMobileMenu={() => setMobileMenuOpen(true)}
                />
                <main className="main-content">
                    <Routes>
                        {availableSections.map(section => (
                            <Route key={section.path} path={section.path} element={renderSectionElement(section)} />
                        ))}
                        {defaultSection && (
                            <Route index element={renderSectionElement(defaultSection)} />
                        )}
                    </Routes>
                </main>
            </div>
        </div>
    );
};

export default AdminApp;
